import type { DocumentTypeCreationFailedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeCreationFailed';
import type { DocumentTypeRenamingFailedAction } from '@Application/Documents/DocumentTypes/Actions/documentTypeRenamingFailed';
import CreateDocumentType from '@Application/Documents/DocumentTypes/Commands/CreateDocumentType';
import RenameDocumentType from '@Application/Documents/DocumentTypes/Commands/RenameDocumentType';
import createDocumentType from '@Application/Documents/DocumentTypes/createDocumentType';
import renameDocumentType from '@Application/Documents/DocumentTypes/renameDocumentType';
import { theme } from '@Guidelines/Theme/theme';
import documentTypeSidebarSignal, {
	closeDocumentTypeSidebar,
} from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Signals/documentTypeSidebarSignal';
import DocumentTypeFormData from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTypeSidebar/FormData/DocumentTypeFormData';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, InputText, Sidebar } from '@verclam/ui-kit';

const scopeNoteStyle = css`
	margin: 0 0 1rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

/**
 * One panel for both creation and rename, told apart by `context.mode` on the signal. `rename` reads
 * the targeted type from the already-loaded `documentTypesStore` rather than from a dedicated fetch —
 * the catalogue is always loaded whole, never paginated.
 */
const DocumentTypeSidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(documentTypeSidebarSignal);
	const documentTypes = useSelector((state) => state.documentTypesStore.documentTypes);
	const { user } = useStrictAuthenticatedUser();

	const [formData, setFormData] = React.useState(() => DocumentTypeFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const documentTypeId = context?.mode === 'rename' ? context.documentTypeId : null;
	const editedDocumentType = documentTypes?.findById(documentTypeId) ?? null;

	// `rename` loads the type it targets; `create` always starts empty. Falling through to
	// `createEmpty()` here would silently wipe the prefill the moment `editedDocumentType` resolves.
	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (context.mode === 'rename') {
			setFormData(
				editedDocumentType !== null
					? DocumentTypeFormData.fromView(editedDocumentType)
					: DocumentTypeFormData.createEmpty(),
			);
			return;
		}

		setFormData(DocumentTypeFormData.createEmpty());
	}, [context, editedDocumentType]);

	const handleLabelChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLabel(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		// Defensive guard: opening this sidebar (creation or rename) already requires an organization of
		// the caller's own account, which itself requires an account — this branch is never reached in
		// practice.
		if (user.account === null) {
			return;
		}

		const accountId = user.account.id;

		setIsSubmitting(true);

		if (context?.mode === 'rename') {
			dispatch(
				renameDocumentType(new RenameDocumentType(accountId, context.documentTypeId, formData.label)),
			)
				.then(() => closeDocumentTypeSidebar())
				.catch((action: DocumentTypeRenamingFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'DOCUMENT_TYPE_RENAMING_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(createDocumentType(new CreateDocumentType(accountId, formData.label)))
			.then(() => closeDocumentTypeSidebar())
			.catch((action: DocumentTypeCreationFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'DOCUMENT_TYPE_CREATION_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, context, user, dispatch]);

	const header =
		context?.mode === 'rename' ? 'Renommer le type de document' : 'Ajouter un type de document';
	const formLabel =
		context?.mode === 'rename' ? 'Renommer un type de document' : 'Créer un type de document';
	// A type is scoped to the account, never to a single organization: every organization sees and
	// affects it. Told up front, next to the field, rather than only at the moment of confirmation.
	const scopeNote =
		context?.mode === 'rename'
			? 'Le nouveau libellé s’appliquera à toutes les structures du compte.'
			: 'Ce type est ajouté pour tout le compte : chaque structure pourra ensuite lui affecter un gabarit.';

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeDocumentTypeSidebar}
			position="right"
			header={header}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeDocumentTypeSidebar}
					/>
					<Button type="submit" form={formId} label="Enregistrer" loading={isSubmitting} />
				</React.Fragment>
			}
			blockScroll
		>
			<p css={scopeNoteStyle}>{scopeNote}</p>
			<Form id={formId} onSubmit={handleSubmit} aria-label={formLabel}>
				<InputText
					name="label"
					label="Libellé"
					required
					value={formData.label}
					onChange={handleLabelChange}
					errorMessage={formData.getValidationMessage('label')}
					isFormFieldValid={formData.isValid('label')}
				/>
			</Form>
		</Sidebar>
	);
};

export default DocumentTypeSidebar;
